import {
  Inline,
  mediaHelper,
  paletteHelper,
  spacingHelper,
  Text,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { FunctionComponent } from 'react';
import styled, { css } from 'styled-components';
import { WebLink } from '../../app-kit/components/WebLink/WebLink';
import { mapApiWeblinkIconInfoToLinkIconInfo } from '../../app-kit/helpers/mapApiWeblinkIconInfoToLinkIconInfo';
import { mapApiWeblinkVariantToLinkVariant } from '../../app-kit/helpers/mapApiWeblinkVariantToLinkVariant';
import { mapBadgeVariant } from '../../app-kit/helpers/mapBadgeVariant';
import { deepOmitTypeName } from '../../helpers/deepOmitTypeName';
import { EllipsisIcon } from './subcomponents/EllipsisIcon';
import { EllipsisIconFragment, PaginationFragment, WebLinkFragment } from '../../types';

const PREV_NEXT_BUTTON_WIDTH = '8.25rem';

const SummaryMessage = styled(Text)`
  ${typographyHelper('body02')};
  color: ${paletteHelper('textSecondary')};
  margin: ${spacingHelper('small')} ${spacingHelper('0')};
`;
const buttonSelected = css`
  background-color: ${paletteHelper('backgroundPrimaryInverted')};
  color: ${paletteHelper('textInverted')};
  &:focus {
    background-color: ${paletteHelper('backgroundPrimaryInverted')};
    color: ${paletteHelper('textInverted')};
  }
`;
const PageNumberLink = styled(WebLink)`
  padding: ${spacingHelper('0')} ${spacingHelper('small')};
  ${({ variant }) => variant === 'button-secondary' && buttonSelected}
`;

const PageNumberLinksAndEllipses = styled(Inline)`
  color: ${paletteHelper('textSecondary')};
`;

const PrevNextButtonLink = styled(WebLink)`
  width: ${PREV_NEXT_BUTTON_WIDTH};

  ${mediaHelper('mobile')} {
    padding-left: 0;
    padding-right: 0;
    span {
      display: none;
    }
    max-width: ${spacingHelper('extraLarge')};
  }
`;

const NextButtonLinkPlaceHolder = styled.div`
  width: ${PREV_NEXT_BUTTON_WIDTH};
`;

type EllipsisIcon = {
  key: string;
  type: 'ellipsisIcon';
  variant: 'default' | 'inverted';
};

type Pagination_WebLinkOrEllipsisIcon = WebLinkFragment | EllipsisIconFragment;

interface PaginationProps {
  pagination: PaginationFragment;
}

export const isEllipsisIcon = (
  value: Pagination_WebLinkOrEllipsisIcon,
): value is EllipsisIconFragment => {
  return (value as EllipsisIconFragment).__typename === 'EllipsisIcon';
};

const PaginationButtonsContainer = styled(Inline)`
  justify-content: flex-start;

  ${mediaHelper('mobile')} {
    justify-content: space-between;
  }
`;

const PageNumberLinksAndEllipsesItem: FunctionComponent<{
  linkOrEllipsisIcon: Pagination_WebLinkOrEllipsisIcon & {
    key: string;
  };
}> = ({ linkOrEllipsisIcon }) => {
  if (isEllipsisIcon(linkOrEllipsisIcon)) {
    return (
      <EllipsisIcon
        {...deepOmitTypeName(linkOrEllipsisIcon)}
        key={linkOrEllipsisIcon.key}
        id={'MoreMd'}
      />
    );
  }
  return (
    <PageNumberLink
      {...deepOmitTypeName({
        ...linkOrEllipsisIcon,
        variant: mapApiWeblinkVariantToLinkVariant(linkOrEllipsisIcon.variant),
        iconInfo: linkOrEllipsisIcon.iconInfo
          ? mapApiWeblinkIconInfoToLinkIconInfo(linkOrEllipsisIcon.iconInfo)
          : null,
        callout: linkOrEllipsisIcon.callout
          ? {
              ...linkOrEllipsisIcon.callout,
              variant: mapBadgeVariant(linkOrEllipsisIcon.callout.variant),
            }
          : null,
      })}
      rel="canonical noopener"
      clientSideRouting
      key={linkOrEllipsisIcon.key}
    />
  );
};

export const Pagination: FunctionComponent<PaginationProps> = ({ pagination }) => {
  const { summaryMessage, prev, next, pageNumberLinksAndEllipses } = pagination;

  return (
    <>
      <PaginationButtonsContainer gap="twoExtraSmall" grow={false}>
        {prev && (
          <PrevNextButtonLink
            {...deepOmitTypeName({
              ...prev,
              variant: mapApiWeblinkVariantToLinkVariant(prev.variant),
              iconInfo: prev.iconInfo ? mapApiWeblinkIconInfoToLinkIconInfo(prev.iconInfo) : null,
              callout: prev.callout
                ? {
                    ...prev.callout,
                    variant: mapBadgeVariant(prev.callout.variant),
                  }
                : null,
            })}
            key={prev.key}
            rel="prev noopener"
            aria-label="Previous"
            clientSideRouting
          />
        )}
        <PageNumberLinksAndEllipses gap="twoExtraSmall" grow={false}>
          {pageNumberLinksAndEllipses.map((linkOrEllipsisIcon) => (
            <PageNumberLinksAndEllipsesItem
              linkOrEllipsisIcon={linkOrEllipsisIcon}
              key={linkOrEllipsisIcon.key}
            />
          ))}
        </PageNumberLinksAndEllipses>

        {next ? (
          <PrevNextButtonLink
            {...deepOmitTypeName({
              ...next,
              variant: mapApiWeblinkVariantToLinkVariant(next.variant),
              iconInfo: next.iconInfo ? mapApiWeblinkIconInfoToLinkIconInfo(next.iconInfo) : null,
              callout: next.callout
                ? {
                    ...next.callout,
                    variant: mapBadgeVariant(next.callout.variant),
                  }
                : null,
            })}
            rel="next noopener"
            aria-label="Next"
            clientSideRouting
            key={next.key}
          />
        ) : (
          <NextButtonLinkPlaceHolder />
        )}
      </PaginationButtonsContainer>

      <SummaryMessage>{summaryMessage}</SummaryMessage>
    </>
  );
};
